import Button from '@/components/Button';
import { OrderAttentionItem, OrderAttentionKind } from '@/models/OrderAttention';
import { OrderIncompleteDeliveryReason } from '@/models/OrderIncompleteDelivery';
import { getSupplierOrderStatusTitle } from '@/models/OrderStatus';
import { OrderDetailsPath } from '@/modules/order/paths';
import { ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface NeedsAttentionStatsProps {
  items: OrderAttentionItem[];
  totalCount: number;
  isEstimatedTotalCount?: boolean;
  isLoading: boolean;
  isResolving?: boolean;
  countDisplayLimit?: number;
  estimatedCountDisplayLimit?: number;
  viewAllPath?: string;
  onResolveIncompleteDelivery: (orderId: string, incompleteDeliveryId: string) => Promise<void>;
}

const reasonLabels: Record<OrderIncompleteDeliveryReason, string> = {
  [OrderIncompleteDeliveryReason.ItemsMissing]: 'Items missing',
  [OrderIncompleteDeliveryReason.ItemDamaged]: 'Item damaged',
  [OrderIncompleteDeliveryReason.Other]: 'Other issue',
};

const formatCount = (count: number, estimated?: boolean) =>
  `${count.toLocaleString()}${estimated ? '+' : ''}`;

const formatDisplayedCount = (
  count: number,
  estimated?: boolean,
  limit?: number,
  estimatedLimit?: number,
) => {
  if (estimated && estimatedLimit !== undefined && count > estimatedLimit) {
    return `${estimatedLimit.toLocaleString()}+`;
  }

  if (limit !== undefined && (count > limit || (estimated && count >= limit))) {
    return `${limit.toLocaleString()}+`;
  }

  return formatCount(count, estimated);
};

const formatOccurredAt = (occurredAt: Date) =>
  occurredAt.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  });

const formatWaitingAge = (hoursWaiting: number) => {
  if (hoursWaiting < 48) {
    return `${hoursWaiting} ${hoursWaiting === 1 ? 'hr' : 'hrs'} ago`;
  }

  const daysWaiting = Math.round(hoursWaiting / 24);
  if (daysWaiting < 14) {
    return `${daysWaiting} ${daysWaiting === 1 ? 'day' : 'days'} ago`;
  }

  const weeksWaiting = Math.round(daysWaiting / 7);
  if (weeksWaiting < 8) {
    return `${weeksWaiting} ${weeksWaiting === 1 ? 'week' : 'weeks'} ago`;
  }

  const monthsWaiting = Math.round(daysWaiting / 30);
  if (monthsWaiting < 18) {
    return `${monthsWaiting} ${monthsWaiting === 1 ? 'month' : 'months'} ago`;
  }

  const yearsWaiting = Math.round(monthsWaiting / 12);
  return `${yearsWaiting} ${yearsWaiting === 1 ? 'year' : 'years'} ago`;
};

const getItemLabel = (item: OrderAttentionItem) => {
  if (item.kind === OrderAttentionKind.IncompleteDelivery) {
    return item.incompleteReason ? reasonLabels[item.incompleteReason] : 'Incomplete delivery';
  }

  if (item.hoursWaiting) {
    return `${item.hoursWaiting}h waiting`;
  }

  return getSupplierOrderStatusTitle(item);
};

const getSecondaryLabel = (item: OrderAttentionItem) => {
  if (item.kind === OrderAttentionKind.IncompleteDelivery) {
    return `Marked incomplete - ${getItemLabel(item).toLowerCase()}`;
  }

  return 'Received, not yet confirmed';
};

const getSideLabel = (item: OrderAttentionItem) => {
  if (item.kind === OrderAttentionKind.IncompleteDelivery) {
    return `${getSupplierOrderStatusTitle(item).replace(/^Order /, '')} ${formatOccurredAt(
      item.occurredAt,
    )}`;
  }

  return item.hoursWaiting
    ? formatWaitingAge(item.hoursWaiting)
    : formatOccurredAt(item.occurredAt);
};

const NeedsAttentionStats = ({
  items,
  totalCount,
  isEstimatedTotalCount,
  isLoading,
  isResolving,
  countDisplayLimit,
  estimatedCountDisplayLimit,
  viewAllPath,
  onResolveIncompleteDelivery,
}: NeedsAttentionStatsProps) => {
  const navigate = useNavigate();
  const hasMoreItems = totalCount > (countDisplayLimit ?? items.length);

  if (!isLoading && items.length === 0) {
    return null;
  }

  return (
    <div
      className="bg-white shadow-sm border border-red-300 rounded-xl px-5 py-4"
      data-testid="dashboard-needs-attention"
    >
      <div className="flex items-start justify-between gap-4 border-b border-red-100 pb-3">
        <div className="flex items-center gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <h3 className="text-lg font-semibold text-red-700">Needs your attention</h3>
        </div>

        <div className="max-w-40 text-right text-sm font-semibold text-red-700">
          {isLoading ? (
            <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
          ) : (
            `${formatDisplayedCount(
              totalCount,
              isEstimatedTotalCount,
              countDisplayLimit,
              estimatedCountDisplayLimit,
            )} orders`
          )}
        </div>
      </div>

      <div>
        {isLoading &&
          Array.from({ length: 2 }).map((_, index) => (
            <div
              key={`attention-loading-${index}`}
              className="flex items-center justify-between gap-4 border-b py-3 last:border-none"
            >
              <div>
                <div className="mb-2 h-4 w-40 animate-pulse rounded bg-gray-200" />
                <div className="h-3 w-56 animate-pulse rounded bg-gray-200" />
              </div>
              <div className="h-8 w-20 animate-pulse rounded bg-gray-200" />
            </div>
          ))}

        {!isLoading &&
          items.map((item) => {
            const isIncomplete = item.kind === OrderAttentionKind.IncompleteDelivery;
            const Icon = isIncomplete ? ExclamationTriangleIcon : ClockIcon;

            return (
              <div
                key={
                  isIncomplete
                    ? `${item.kind}-${item.incompleteDeliveryId}`
                    : `${item.kind}-${item.orderId}`
                }
                className="flex flex-col gap-3 border-b py-3 last:border-none sm:flex-row sm:items-center sm:justify-between"
                data-testid="dashboard-needs-attention-item"
                data-order-code={item.orderCode}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => navigate(OrderDetailsPath.withId(item.orderId))}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <Icon className="mt-1 h-5 w-5 shrink-0 text-red-700" />
                    <div className="min-w-0">
                      <div className="truncate text-base font-semibold text-gray-900">
                        {item.orderCode} - {item.storeName}
                      </div>
                      <div className="text-sm font-medium text-gray-600">
                        {getSecondaryLabel(item)}
                      </div>
                    </div>
                  </div>
                </button>

                <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                  <div className="text-sm font-medium text-gray-600">{getSideLabel(item)}</div>

                  {isIncomplete && item.incompleteDeliveryId && (
                    <Button
                      data-testid="dashboard-resolve-incomplete-delivery-button"
                      size="xs"
                      variant="secondary"
                      className="shrink-0 border-gray-300"
                      loading={isResolving}
                      onClick={() =>
                        onResolveIncompleteDelivery(item.orderId, item.incompleteDeliveryId!)
                      }
                    >
                      Resolve
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {!isLoading && viewAllPath && hasMoreItems && (
        <div className="border-t border-red-100 pt-3 text-right">
          <Button
            to={viewAllPath}
            variant="plain"
            size="sm"
            className="text-sm font-semibold text-red-700 hover:text-red-600"
          >
            View all orders
          </Button>
        </div>
      )}
    </div>
  );
};

export default NeedsAttentionStats;
